import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Auto-advancing product screenshot carousel.
 * Props:
 *   slides: [{ src, alt, caption?, theme: 'dark' | 'light' }]
 *   intervalMs: auto-advance interval (default 5000)
 */
export default function Slideshow({ slides, intervalMs = 5000 }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = slides.length;

  useEffect(() => {
    if (paused || n <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % n), intervalMs);
    return () => clearInterval(t);
  }, [paused, n, intervalMs]);

  if (!n) return null;

  const go = (i) => setIndex(((i % n) + n) % n);

  return (
    <div
      className="card-tile p-3 overflow-hidden relative group"
      data-testid="product-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slide track */}
      <div className="relative w-full aspect-[16/10] bg-black overflow-hidden">
        {slides.map((s, i) => (
          <img
            key={i}
            src={s.src}
            alt={s.alt}
            data-testid={`slide-img-${i}`}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            } ${s.theme === "light" ? "bg-white" : "bg-black"}`}
          />
        ))}

        {/* Prev / Next */}
        {n > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(index - 1)}
              data-testid="slideshow-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-black/85 text-white border border-white/15 backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              data-testid="slideshow-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-black/85 text-white border border-white/15 backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Caption pill */}
        {slides[index].caption && (
          <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] bg-black/75 text-white border border-white/15 px-3 py-1.5 backdrop-blur-sm" data-testid="slide-caption">
            {slides[index].caption}
          </div>
        )}
      </div>

      {/* Dots */}
      {n > 1 && (
        <div className="flex justify-center gap-2 mt-4" data-testid="slideshow-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              data-testid={`slideshow-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 transition-all ${
                i === index ? "w-8 bg-[#F4A22A]" : "w-3 bg-white/25 hover:bg-white/45"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
